import { Reference } from '@approvalmax/types';
import { mathService, numberHelpers } from '@approvalmax/utils';
import { domain } from 'modules/data';
import { useEffect, useRef } from 'react';

import { setCurrencyPrecision } from '../../../helpers';
import { useCurrencyPrecision } from '../../../hooks';
import { useExchangeRates } from '../../../hooks/useExchangeRates/useExchangeRates';
import { useExpenseReportExpenseLines } from '../useExpenseReportExpenseLines/useExpenseReportExpenseLines';

export const useExpenseLinesUpdate = (
    request: domain.NetSuiteExpenseReportRequest,
    currency?: Reference | null,
    date?: string | null
) => {
    const { setExpenseLines } = useExpenseReportExpenseLines();

    const currencyPrecision = useCurrencyPrecision(currency);

    const hasDefaultExpenseLines = request.details.expenseLines.length > 0;

    const exchangeRatesFirstCall = useRef<boolean>(true);

    const { refetch: fetchExchangeRatesToRequestCurrency } = useExchangeRates(
        request.companyId,
        date,
        currency?.id,
        true
    );

    useEffect(() => {
        if (!currency) {
            return;
        }

        (async () => {
            const { data: exchangeRates } = await fetchExchangeRatesToRequestCurrency();

            if (!exchangeRates?.rates) {
                return;
            }

            /**
             * If it's first call of exchange rates and
             * there are default expense lines
             * then it's opening form for edit and we should skip expense lines update
             */
            if (hasDefaultExpenseLines && exchangeRatesFirstCall.current) {
                exchangeRatesFirstCall.current = false;

                return;
            }

            exchangeRatesFirstCall.current = false;

            setExpenseLines((current) => {
                return current.map((line) => {
                    const lineCurrencyRate = exchangeRates.rates.find((rate) => rate.id === line.currency?.id);

                    if (!line.currency || !lineCurrencyRate?.rateValue) {
                        return line;
                    }

                    const newLine = {
                        ...line,
                    };

                    newLine.exchangeRate = lineCurrencyRate?.rateValue ?? 0;

                    if (numberHelpers.isNumber(newLine.foreignAmount)) {
                        const amount = setCurrencyPrecision(
                            newLine.foreignAmount * newLine.exchangeRate,
                            currencyPrecision
                        );

                        const taxAmount = line.taxRate
                            ? setCurrencyPrecision(mathService.multiply(amount, line.taxRate / 100), currencyPrecision)
                            : 0;

                        const grossAmount = setCurrencyPrecision(amount + taxAmount, currencyPrecision);

                        newLine.amount = amount;
                        newLine.taxAmount = taxAmount;
                        newLine.grossAmount = grossAmount;
                    }

                    return newLine;
                });
            });
        })();
    }, [
        currency,
        hasDefaultExpenseLines,
        exchangeRatesFirstCall,
        currencyPrecision,
        setExpenseLines,
        fetchExchangeRatesToRequestCurrency,
    ]);
};
